import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import { Package, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getMyInventoryAdjustments } from '../../services/api';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const Pagination = ({ page, pages, total, limit, onPageChange }) => {
  if (total <= limit) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
      <span className="text-xs text-slate-500">{from}-{to} de {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500 px-2 py-1">{page}/{pages}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const typeBadge = (type) => {
  const map = {
    absolute: { label: 'Absoluto', color: 'bg-slate-100 text-slate-700' },
    increment: { label: 'Incremento', color: 'bg-green-100 text-green-700' },
    decrement: { label: 'Decremento', color: 'bg-red-100 text-red-700' },
  };
  const badge = map[type] || { label: type || '\u2014', color: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.color}`}>{badge.label}</span>;
};

const AjustesInventario = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [adjustmentType, setAdjustmentType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (adjustmentType) params.adjustment_type = adjustmentType;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + 'T23:59:59').toISOString();
      const res = await getMyInventoryAdjustments(params);
      setData(res.data);
    } catch { toast.error('Error cargando ajustes de inventario'); }
    finally { setLoading(false); }
  }, [page, adjustmentType, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Package className="w-7 h-7 text-teal-600" />
          Ajustes de Inventario
        </h1>
        <p className="text-slate-500 mt-1">Ajustes de inventario sincronizados desde tu POS</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 items-center">
          <select value={adjustmentType} onChange={e => { setAdjustmentType(e.target.value); setPage(1); }}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Todos los tipos</option>
            <option value="absolute">Absoluto</option>
            <option value="increment">Incremento</option>
            <option value="decrement">Decremento</option>
          </select>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Desde</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Hasta</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          {(adjustmentType || dateFrom || dateTo) && (
            <button onClick={() => { setAdjustmentType(''); setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline">Limpiar</button>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Producto</th>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-center px-4 py-3">Tipo</th>
                <th className="text-right px-4 py-3">Cantidad</th>
                <th className="text-left px-4 py-3">Razón</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">No hay ajustes de inventario</td></tr>
              )}
              {data.items.map((item, idx) => (
                <tr key={item.id || idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-700">{item.product_name || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{item.product_sku || '\u2014'}</td>
                  <td className="px-4 py-3 text-center">{typeBadge(item.adjustment_type)}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-700">{item.quantity ?? '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-xs truncate">{item.reason || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500">{item.source || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(item.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} pages={data.pages} total={data.total} limit={50} onPageChange={setPage} />
      </div>
    </DashboardLayout>
  );
};

export default AjustesInventario;
